import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Music, Plus, Sparkles, X } from 'lucide-react'
import { getAudios, getAudioPlaylists } from '@meno/ui/lib/api'
import { TRANSITIONS } from '@/lib/transitions'
import type { Companions } from '@/components/companionsModel'
import CompanionPickerDialog, { type CompanionOption } from '@/components/CompanionPickerDialog'
import TransitionPickerDialog from '@/components/TransitionPickerDialog'

type AudioRow = { id: number; original_name: string }
type NamedRow = { id: number; name: string }

// Publish companions (audio / transition) for a playlist or layout. The audio
// tab opens a modal list picker; the transition tab opens a visual preview
// modal. (Stop screens were removed — they apply only to tablets, so they don't
// belong in playlist/layout creation.)
export default function CompanionSection({
  value, onChange,
}: {
  value: Companions
  onChange: (next: Companions) => void
}) {
  const { t } = useTranslation()
  const loadedRef = useRef(false)
  const [tab, setTab] = useState<'audio' | 'transition'>('audio')
  const [pickerOpen, setPickerOpen] = useState(false)          // audio list picker
  const [transitionOpen, setTransitionOpen] = useState(false)  // transition preview picker
  const [audios, setAudios] = useState<AudioRow[]>([])
  const [audioPlaylists, setAudioPlaylists] = useState<NamedRow[]>([])

  useEffect(() => {
    if (loadedRef.current) return
    loadedRef.current = true
    getAudios().then(r => setAudios(r.data || [])).catch(() => {})
    getAudioPlaylists().then(r => setAudioPlaylists(r.data || [])).catch(() => {})
  }, [])

  const audioValue = value.audioId != null
    ? `a:${value.audioId}`
    : value.audioPlaylistId != null ? `p:${value.audioPlaylistId}` : null
  const setAudioValue = (v: string | null) => {
    if (v?.startsWith('a:')) onChange({ ...value, audioId: Number(v.slice(2)), audioPlaylistId: null })
    else if (v?.startsWith('p:')) onChange({ ...value, audioId: null, audioPlaylistId: Number(v.slice(2)) })
    else onChange({ ...value, audioId: null, audioPlaylistId: null })
  }

  const audioLabel = value.audioId != null
    ? audios.find(a => a.id === value.audioId)?.original_name ?? t('companions.audio', 'Audio')
    : value.audioPlaylistId != null
      ? audioPlaylists.find(p => p.id === value.audioPlaylistId)?.name ?? t('companions.audioPlaylist', 'Audio playlist')
      : null
  const transitionLabel = value.transition
    ? TRANSITIONS.find(tr => tr.key === value.transition)?.name ?? value.transition
    : null

  const activeLabel = tab === 'audio' ? audioLabel : transitionLabel
  const activeHasValue = activeLabel != null

  const openActivePicker = () => {
    if (tab === 'audio') setPickerOpen(true)
    else setTransitionOpen(true)
  }

  const clearActive = () => {
    if (tab === 'audio') setAudioValue(null)
    else onChange({ ...value, transition: null })
  }

  const audioOptions: CompanionOption[] = [
    ...audioPlaylists.map(p => ({ value: `p:${p.id}`, label: p.name, group: t('companions.audioPlaylists', 'Audio playlists') })),
    ...audios.map(a => ({ value: `a:${a.id}`, label: a.original_name, group: t('companions.audioFiles', 'Audio files') })),
  ]

  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <div className="flex items-center justify-between gap-2 border-b border-border px-3">
        <div className="flex gap-1 overflow-x-auto no-scrollbar">
          <CompanionTab
            active={tab === 'audio'}
            onClick={() => setTab('audio')}
            icon={Music}
            label={t('companions.audio', 'Audio')}
            added={value.audioId != null || value.audioPlaylistId != null}
          />
          <CompanionTab
            active={tab === 'transition'}
            onClick={() => setTab('transition')}
            icon={Sparkles}
            label={t('companions.transition', 'Transition')}
            added={value.transition != null}
          />
        </div>
        <button
          type="button"
          onClick={openActivePicker}
          aria-label={t('companions.add', 'Add')}
          className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-border bg-background text-foreground hover:bg-muted transition-colors shrink-0"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      <div className="p-3">
        <button
          type="button"
          onClick={openActivePicker}
          className="w-full flex items-center gap-2 rounded-md bg-card px-2.5 py-2 text-start hover:bg-muted/50 transition-colors"
        >
          {tab === 'audio'
            ? <Music className="w-4 h-4 text-muted-foreground shrink-0" />
            : <Sparkles className="w-4 h-4 text-muted-foreground shrink-0" />}
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium truncate">
              {activeLabel ?? t('companions.none', 'None')}
            </p>
            <p className="text-[10px] text-muted-foreground truncate">
              {activeHasValue
                ? t('companions.publishesTogether', 'Publishes together')
                : t('companions.tapToChoose', 'Tap to choose')}
            </p>
          </div>
          {activeHasValue && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); clearActive() }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); clearActive() } }}
              aria-label={t('companions.clear', 'Remove')}
              className="w-7 h-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
        </button>
      </div>

      {/* Audio — list picker. */}
      <CompanionPickerDialog
        open={pickerOpen}
        title={t('companions.audio', 'Audio')}
        options={audioOptions}
        selected={audioValue}
        onSelect={setAudioValue}
        onClose={() => setPickerOpen(false)}
      />
      {/* Transition — visual preview picker. */}
      <TransitionPickerDialog
        open={transitionOpen}
        selected={value.transition}
        onSelect={(key) => onChange({ ...value, transition: key })}
        onClose={() => setTransitionOpen(false)}
      />
    </div>
  )
}

function CompanionTab({
  active, onClick, icon: Icon, label, added,
}: {
  active: boolean
  onClick: () => void
  icon: typeof Music
  label: string
  added: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-10 px-2.5 text-xs font-medium border-b-2 -mb-px flex items-center gap-1.5 whitespace-nowrap transition-colors ${
        active ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
      {added && <span className="h-1.5 w-1.5 rounded-full bg-primary" />}
    </button>
  )
}
